/**
 * DENEB London Theme - Cart Drawer
 *
 * Extracted from sections/cart-drawer.liquid (Task 20c) so the cart drawer JS
 * can be cached as a static asset instead of re-sent inline on every page view
 * (the drawer renders in the layout on every page).
 *
 * Dynamic Liquid values are passed in via the
 * <script type="application/json" data-cart-drawer-config> element rendered by
 * the section, and parsed in readCartDrawerConfig():
 *   - allProductsUrl -> routes.all_products_collection_url (used by showEmptyCart)
 *
 * Globals exposed for cross-section consumers (assigned INSIDE init so they
 * close over the freshly-captured drawer element after editor re-render):
 *   - window.openCartDrawer  -> header.liquid onclick=, product.js (after add)
 *   - window.closeCartDrawer
 *   - window.updateCartCount
 *   - window.refreshCartData -> quick-view-modal.liquid (after add)
 *
 * Editor-safe init: initialises once on normal page load, and re-initialises
 * against fresh DOM on shopify:section:load. The document-level Escape keydown
 * listener is bound only once (guarded by documentListenersBound) and reads the
 * live drawer element at event time so it never operates on a stale reference.
 */
(function () {
  'use strict';

  var documentListenersBound = false;

  function readCartDrawerConfig() {
    var configEl = document.querySelector('[data-cart-drawer-config]');
    if (!configEl) return {};
    try {
      return JSON.parse(configEl.textContent) || {};
    } catch (e) {
      return {};
    }
  }

  function initCartDrawer() {
    var drawer = document.getElementById('cart-drawer');
    if (!drawer) return;

    var config = readCartDrawerConfig();
    var allProductsUrl = config.allProductsUrl || '/collections/all';
    var cartUrl = config.cartUrl || '/cart';
    var cartChangeUrl = config.cartChangeUrl || '/cart/change.js';

    // Flag to prevent overlapping quantity updates
    var isUpdatingQuantity = false;

    var content = drawer.querySelector('[data-cart-drawer-content]');
    var closeTriggers = drawer.querySelectorAll('[data-cart-drawer-close]');

    // Focus management: remember what triggered the drawer open
    var lastFocused = null;
    var liveRegion = drawer.querySelector('[data-cart-live]');

    function announceCart(message) {
      if (!liveRegion) return;
      liveRegion.textContent = '';
      requestAnimationFrame(function () { liveRegion.textContent = message; });
    }

    // Open drawer function (globally accessible)
    window.openCartDrawer = function() {
      lastFocused = document.activeElement;
      drawer.classList.add('is-open');
      drawer.setAttribute('aria-hidden', 'false');
      document.body.style.overflow = 'hidden';

      // Refresh cart data; focus close button once content is ready
      refreshCartData().then(function () {
        var closeBtn = drawer.querySelector('button[data-cart-drawer-close]');
        if (closeBtn) closeBtn.focus();
      });
    };

    // Close drawer function
    function closeCartDrawer() {
      drawer.classList.remove('is-open');
      drawer.setAttribute('aria-hidden', 'true');
      document.body.style.overflow = '';
      if (lastFocused && document.contains(lastFocused)) lastFocused.focus();
    }

    // Close triggers
    closeTriggers.forEach(trigger => {
      trigger.addEventListener('click', closeCartDrawer);
    });

    // Refresh cart data from server-rendered section HTML
    // Returns a Promise so callers can chain .then() (e.g. focus management on open)
    function refreshCartData() {
      // Add cache-busting to ensure fresh data
      const cacheBuster = new Date().getTime();
      return fetch('/?section_id=cart-drawer&_=' + cacheBuster)
        .then(response => response.text())
        .then(sectionHtml => {
          const parser = new DOMParser();
          const doc = parser.parseFromString(sectionHtml, 'text/html');
          const serverDrawer = doc.getElementById('cart-drawer');

          if (!serverDrawer) {
            console.error('Could not find #cart-drawer in server response');
            return;
          }

          if (!content) {
            console.error('Cart drawer content element not found');
            return;
          }

          const serverContent = serverDrawer.querySelector('[data-cart-drawer-content]');
          if (serverContent) {
            content.innerHTML = serverContent.innerHTML;

            // Force images to load by removing lazy loading
            content.querySelectorAll('img[loading="lazy"]').forEach(img => {
              img.removeAttribute('loading');
            });
          } else {
            console.error('Could not find [data-cart-drawer-content] in server response');
          }

          const serverCount = serverDrawer.querySelector('[data-cart-item-count]');
          if (serverCount) {
            const countText = serverCount.textContent || '';
            const count = parseInt(countText.replace(/[^\d]/g, ''), 10) || 0;
            updateCartCount(count);
          }

          attachCartListeners();
          document.dispatchEvent(new CustomEvent('cart:updated'));
        })
        .catch(error => {
          console.error('Error refreshing cart drawer:', error);
        });
    }

    // Attach event listeners to cart items
    function attachCartListeners() {
      // Quantity change handlers
      drawer.querySelectorAll('[data-quantity-change]').forEach(btn => {
        btn.addEventListener('click', handleQuantityChange);
      });

      // Direct quantity input
      drawer.querySelectorAll('[data-quantity-input]').forEach(input => {
        input.addEventListener('change', handleQuantityInput);
      });

      // Remove item
      drawer.querySelectorAll('[data-remove-item]').forEach(btn => {
        btn.addEventListener('click', handleRemoveItem);
      });

      // Close handlers for content that can be re-rendered
      if (content) {
        content.querySelectorAll('[data-cart-drawer-close]').forEach(trigger => {
          trigger.addEventListener('click', closeCartDrawer);
        });
      }

      if (typeof window.initDenebPromoCode === 'function') {
        window.initDenebPromoCode();
      }
    }

    // Handle quantity button clicks
    function handleQuantityChange() {
      // Prevent overlapping requests
      if (isUpdatingQuantity) return;

      const item = this.closest('[data-cart-item]');
      const input = item.querySelector('[data-quantity-input]');
      const key = item.dataset.itemKey;
      const change = parseInt(this.dataset.quantityChange);
      const currentVal = parseInt(input.value) || 1;
      const newQuantity = currentVal + change;
      const maxInventory = parseInt(item.dataset.inventoryQuantity) || 999;

      // Check minimum
      if (newQuantity < 1) return;

      // Check maximum inventory
      if (newQuantity > maxInventory) {
        // Show warning or disable button
        showCartDrawerError("Sorry, we only have " + maxInventory + " in stock");
        this.disabled = true;
        this.style.opacity = '0.4';
        return;
      }

      // Set flag and disable buttons during update
      isUpdatingQuantity = true;
      const qtyButtons = item.querySelectorAll('[data-quantity-change]');
      qtyButtons.forEach(btn => {
        btn.disabled = true;
        btn.style.opacity = '0.4';
      });

      input.value = newQuantity;
      updateCartItem(key, newQuantity, item);
    }

    // Handle quantity input changes
    function handleQuantityInput() {
      const item = this.closest('[data-cart-item]');
      const key = item.dataset.itemKey;
      const maxInventory = parseInt(item.dataset.inventoryQuantity) || 999;
      let newQuantity = parseInt(this.value) || 1;

      if (newQuantity < 1) {
        this.value = 1;
        newQuantity = 1;
      }

      if (newQuantity > maxInventory) {
        showCartDrawerError("Sorry, we only have " + maxInventory + " in stock");
        this.value = maxInventory;
        newQuantity = maxInventory;
      }

      updateCartItem(key, newQuantity, item);
    }

    // Show a transient error message inside the cart drawer
    function showCartDrawerError(message) {
      let note = drawer.querySelector('[data-cart-drawer-note]');
      if (!note) {
        note = document.createElement('p');
        note.setAttribute('data-cart-drawer-note', '');
        note.setAttribute('role', 'status');
        note.className = 'cart-drawer__note cart-drawer__note--error';
        const contentEl = drawer.querySelector('[data-cart-drawer-content]');
        if (contentEl) contentEl.parentNode.insertBefore(note, contentEl);
      }
      note.textContent = message;
      if (note._clearTimer) clearTimeout(note._clearTimer);
      note._clearTimer = setTimeout(() => { note.textContent = ''; }, 4000);
    }

    // Handle remove item
    function handleRemoveItem() {
      const item = this.closest('[data-cart-item]');
      const key = item.dataset.itemKey;

      item.classList.add('is-updating');

      fetch(cartChangeUrl, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Accept': 'application/json'
        },
        body: JSON.stringify({
          id: key,
          quantity: 0
        })
      })
      .then((response) =>
        response.json().then((data) => {
          if (!response.ok) {
            throw new Error(data.description || 'Could not update cart');
          }
          return data;
        })
      )
      .then(() => {
        announceCart('Item removed');
        refreshCartData();
        item.classList.remove('is-updating');
      })
      .catch((error) => {
        console.error('Error removing item:', error);
        item.classList.remove('is-updating');
        showCartDrawerError(error.message || 'Could not update cart');
        refreshCartData();
      });
    }

    // Update cart count
    function updateCartCount(count) {
      // Update drawer count
      const countElements = document.querySelectorAll('[data-cart-item-count]');
      countElements.forEach(el => {
        el.textContent = '(' + count + ')';
      });

      // Update or create header badge
      const headerCartBtns = document.querySelectorAll('.header__icon--cart');
      headerCartBtns.forEach(btn => {
        let badge = btn.querySelector('.header__cart-count');
        if (!badge && count > 0) {
          // Create badge if it doesn't exist - CSS will handle styling
          badge = document.createElement('span');
          badge.className = 'header__cart-count';
          btn.appendChild(badge);
        }
        if (badge) {
          badge.textContent = count;
          badge.style.display = count > 0 ? 'flex' : 'none';
        }
      });

      // Also update existing header badges (if any were rendered server-side)
      const headerCounts = document.querySelectorAll('.header__cart-count');
      headerCounts.forEach(el => {
        el.textContent = count;
        el.style.display = count > 0 ? 'flex' : 'none';
      });
    }

    // Update cart item quantity
    function updateCartItem(key, quantity, itemElement) {
      itemElement.classList.add('is-updating');

      fetch(cartChangeUrl, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Accept': 'application/json'
        },
        body: JSON.stringify({
          id: key,
          quantity: quantity
        })
      })
      .then((response) =>
        response.json().then((data) => {
          if (!response.ok) {
            throw new Error(data.description || 'Could not update cart');
          }
          return data;
        })
      )
      .then(() => {
        announceCart('Quantity updated');
        refreshCartData();
      })
      .catch((error) => {
        console.error('Error updating cart:', error);
        showCartDrawerError(error.message || 'Could not update cart');
        refreshCartData();
      })
      .finally(() => {
        itemElement.classList.remove('is-updating');
        // Re-enable quantity buttons and reset flag
        const qtyButtons = itemElement.querySelectorAll('[data-quantity-change]');
        qtyButtons.forEach(btn => btn.disabled = false);
        isUpdatingQuantity = false;
      });
    }

    // Show empty cart state
    function showEmptyCart() {
      const content = drawer.querySelector('[data-cart-drawer-content]');
      if (content) {
        content.innerHTML = `
          <div class="cart-drawer__empty">
            <svg class="cart-drawer__empty-icon" width="64" height="64" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="1">
              <path d="M6 2L3 6v14a2 2 0 0 0 2 2h14a2 2 0 0 0 2-2V6l-3-4z"></path>
              <line x1="3" y1="6" x2="21" y2="6"></line>
              <path d="M16 10a4 4 0 0 1-8 0"></path>
            </svg>
            <h3 class="cart-drawer__empty-title">Your bag is empty</h3>
            <p class="cart-drawer__empty-text">Looks like you haven't added anything to your bag yet.</p>
            <a href="${allProductsUrl}" class="cart-drawer__shop-btn" data-cart-drawer-close>Start Shopping</a>
          </div>
        `;

        // Re-attach close handlers to new elements
        content.querySelectorAll('[data-cart-drawer-close]').forEach(trigger => {
          trigger.addEventListener('click', closeCartDrawer);
        });
      }
    }

    // Make functions globally accessible
    window.closeCartDrawer = closeCartDrawer;
    window.updateCartCount = updateCartCount;
    window.refreshCartData = refreshCartData;

    // Tab trap: keep focus within the open drawer. One listener per init;
    // safe because the drawer element is replaced on editor re-render.
    drawer.addEventListener('keydown', function (e) {
      if (e.key !== 'Tab' || !drawer.classList.contains('is-open')) return;
      var focusables = drawer.querySelectorAll('a[href], button:not([disabled]), input, select, textarea, [tabindex]:not([tabindex="-1"])');
      if (!focusables.length) return;
      var first = focusables[0];
      var last = focusables[focusables.length - 1];
      if (e.shiftKey && document.activeElement === first) {
        e.preventDefault();
        last.focus();
      } else if (!e.shiftKey && document.activeElement === last) {
        e.preventDefault();
        first.focus();
      }
    });

    // Document-level keyboard listener is bound only once so editor section
    // reloads don't stack duplicate handlers. It reads the live drawer from
    // the DOM at event time (the closure-captured `drawer` is replaced on
    // shopify:section:load, so query fresh).
    if (!documentListenersBound) {
      documentListenersBound = true;

      // Close on escape key — delegates to window.closeCartDrawer so that
      // focus is restored to the element that opened the drawer.
      document.addEventListener('keydown', function (e) {
        if (e.key !== 'Escape') return;
        const liveDrawer = document.getElementById('cart-drawer');
        if (liveDrawer && liveDrawer.classList.contains('is-open')) {
          if (typeof window.closeCartDrawer === 'function') {
            window.closeCartDrawer();
          } else {
            liveDrawer.classList.remove('is-open');
            liveDrawer.setAttribute('aria-hidden', 'true');
            document.body.style.overflow = '';
          }
        }
      });
    }

    // Initialize event listeners on page load
    attachCartListeners();
  }

  // Run on DOMContentLoaded or immediately if already loaded
  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', initCartDrawer);
  } else {
    initCartDrawer();
  }

  // Re-init against fresh DOM when the section is reloaded in the theme editor
  document.addEventListener('shopify:section:load', function (e) {
    if (e.target.querySelector('#cart-drawer') || e.target.id === 'cart-drawer') {
      initCartDrawer();
    }
  });
})();
